/**
 * Editor billing details (spec #298): legal name, invoice address, IBAN or a
 * bank account without IBAN (bank name + account number), SWIFT/BIC,
 * account holder, tax number, VAT status - where to pay an editor, kept in
 * the company DB (`editor-billing-details-routes.ts`). Two pieces live here:
 *
 *   - `EditorBillingDetailsScreen` - the profile's "Billing details" section,
 *     view/edit/save at any time. Mounted inside `EditorScreen`'s dashboard in
 *     place of the normal rows while open, same pattern as `EditorBilling`
 *     (pay) and `AbsenceCard`.
 *   - `BillingDetailsPrompt` - the first-open nudge: any signed-in editor
 *     whose details are missing sees it on the dashboard; "Later" closes it
 *     for this visit only (`billingDetailsPrompt.ts`, mirrors
 *     `app/passwordNudge.ts`'s own dismiss), and it returns on the next visit
 *     until the details are saved.
 *
 * @module web/screens/BillingDetails
 */
import { HubPageHead } from "./EditorHub.tsx";
import { useCallback, useEffect, useState } from "react";
import { ApiError, get, put } from "../api/client.ts";
import type { EditorBillingDetails, EditorBillingDetailsResponse, EditorPayoutMethod, EditorVatStatus } from "../api/types.ts";
import { AppSelect } from "../components/AppSelect.tsx";
import { countryOptions, toCountryCode } from "../countryCodes.ts";
import { intlLocale } from "../i18n/core.ts";
import {
  emptyBillingDetailsDraft,
  isValidIban,
  maskIban,
  normalizeAccountNumber,
  normalizeBic,
  normalizeIban,
  validateBillingDetailsDraft,
  type BillingDetailsDraft,
  type BillingDetailsFieldErrors,
} from "./billingDetails.ts";
import { useI18n, type I18n, type MessageKey } from "../i18n/index.tsx";
import { browserVisitStore, readBillingDetailsPromptDismissed, rememberBillingDetailsPromptDismissed } from "./billingDetailsPrompt.ts";

const VAT_STATUS_OPTIONS: { value: EditorVatStatus; key: MessageKey }[] = [
  { value: "small_business", key: "billing.vat_small_business" },
  { value: "vat_registered", key: "billing.vat_registered" },
  { value: "none", key: "billing.vat_none" },
];

const PAYOUT_METHOD_OPTIONS: { value: EditorPayoutMethod; key: MessageKey }[] = [
  { value: "iban", key: "billing.method_iban" },
  { value: "bank_account", key: "billing.method_bank_account" },
];

/**
 * `validateBillingDetailsDraft` (billingDetails.ts) decides WHICH fields are
 * wrong; the screen words it in the editor's language. A field with a format
 * rule has two reasons: missing (REQUIRED_KEYS), or present but malformed
 * (INVALID_KEYS). The BIC is optional, so it only ever has the second.
 */
const REQUIRED_KEYS: Partial<Record<keyof BillingDetailsFieldErrors, MessageKey>> = {
  legalName: "billing.req_legal_name",
  addressLine1: "billing.req_address",
  postalCode: "billing.req_postal_code",
  city: "billing.req_city",
  country: "billing.req_country",
  accountHolder: "billing.req_account_holder",
  iban: "billing.req_iban",
  bankName: "billing.req_bank_name",
  accountNumber: "billing.req_account_number",
};
const INVALID_KEYS: Partial<Record<keyof BillingDetailsFieldErrors, MessageKey>> = {
  iban: "billing.invalid_iban",
  bankName: "billing.invalid_bank_name",
  accountNumber: "billing.invalid_account_number",
  bic: "billing.invalid_bic",
};

function toDraft(d: EditorBillingDetails | null): BillingDetailsDraft {
  if (!d) return emptyBillingDetailsDraft();
  return {
    legalName: d.legalName,
    addressLine1: d.addressLine1,
    addressLine2: d.addressLine2 ?? "",
    postalCode: d.postalCode,
    city: d.city,
    country: toCountryCode(d.country),
    // Rows saved before the bank-account option have no payoutMethod: they are IBAN profiles.
    payoutMethod: d.payoutMethod === "bank_account" ? "bank_account" : "iban",
    iban: d.iban ?? "",
    bankName: d.bankName ?? "",
    accountNumber: d.accountNumber ?? "",
    bic: d.bic ?? "",
    accountHolder: d.accountHolder,
    taxNumber: d.taxNumber ?? "",
    vatId: d.vatId ?? "",
    vatStatus: d.vatStatus ?? "",
  };
}

function saveErrorMessage(err: unknown, t: I18n["t"]): string {
  if (!(err instanceof ApiError)) return t("billing.err_save");
  if (err.message === "invalid_iban") return t("billing.err_invalid_iban_server");
  if (err.message === "missing_required_fields") return t("billing.err_missing_required");
  if (err.message === "invalid_bank_name") return t("billing.invalid_bank_name");
  if (err.message === "invalid_account_number") return t("billing.invalid_account_number");
  if (err.message === "account_number_is_iban") return t("billing.account_number_is_iban");
  if (err.message === "invalid_bic") return t("billing.invalid_bic");
  if (err.message === "invalid_payout_method") return t("billing.err_save");
  // Anything else is the company DB's own rejection reason (spec #298: "a DB
  // rejection is shown, not swallowed") - shown verbatim rather than mapped to
  // a generic sentence, since it is the one source that actually knows why.
  return err.message;
}

function Field({ label, value, onChange, error, disabled, optional }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  disabled?: boolean;
  optional?: boolean;
}) {
  const { t } = useI18n();
  return (
    <label className="ed-avail-field">
      <span>{optional ? t("billing.field_optional", { label }) : label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      {error && <span className="editor-offer-error">{error}</span>}
    </label>
  );
}

export function EditorBillingDetailsScreen({ onBack, backLabel, onSaved }: { onBack: () => void; backLabel?: string; onSaved?: () => void }) {
  const [draft, setDraft] = useState<BillingDetailsDraft | null>(null);
  /** The saved IBAN / account number shows masked until the editor chooses to change it. */
  const [numberEditing, setNumberEditing] = useState(false);
  const [errors, setErrors] = useState<BillingDetailsFieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { t, locale } = useI18n();
  const fieldError = (key: keyof BillingDetailsFieldErrors): string | undefined => {
    if (!errors[key]) return undefined;
    const value = draft ? String(draft[key as keyof BillingDetailsDraft] ?? "").trim() : "";
    if (key === "accountNumber" && value && isValidIban(normalizeAccountNumber(value))) return t("billing.account_number_is_iban");
    const msg = (value && INVALID_KEYS[key]) || REQUIRED_KEYS[key] || INVALID_KEYS[key];
    return msg ? t(msg) : undefined;
  };

  const load = useCallback(async () => {
    try {
      const resp = await get<EditorBillingDetailsResponse>("/api/editor/billing-details");
      setDraft(toDraft(resp.details));
      setNumberEditing(!resp.details);
    } catch {
      setDraft(emptyBillingDetailsDraft());
      setNumberEditing(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function set<K extends keyof BillingDetailsDraft>(key: K, value: BillingDetailsDraft[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setSaved(false);
  }

  async function save() {
    if (busy || !draft) return;
    const fieldErrors = validateBillingDetailsDraft(draft);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    setBusy(true);
    setSaveError(null);
    try {
      const saved = await put<EditorBillingDetails>("/api/editor/billing-details", {
        legalName: draft.legalName.trim(),
        addressLine1: draft.addressLine1.trim(),
        addressLine2: draft.addressLine2.trim() || null,
        postalCode: draft.postalCode.trim(),
        city: draft.city.trim(),
        country: draft.country.trim(),
        payoutMethod: draft.payoutMethod,
        iban: draft.payoutMethod === "iban" ? normalizeIban(draft.iban) : null,
        bankName: draft.payoutMethod === "bank_account" ? draft.bankName.trim().replace(/\s+/g, " ") : null,
        accountNumber: draft.payoutMethod === "bank_account" ? normalizeAccountNumber(draft.accountNumber) : null,
        bic: draft.bic.trim() ? normalizeBic(draft.bic) : null,
        accountHolder: draft.accountHolder.trim(),
        taxNumber: draft.taxNumber.trim() || null,
        vatId: draft.vatId.trim() || null,
        vatStatus: draft.vatStatus || null,
      });
      setDraft(toDraft(saved));
      setNumberEditing(false);
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setSaveError(saveErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  if (!draft) {
    return (
      <div className="ed-billing hd-page" data-screen="editor-billing-details">
        <p className="ed-muted">{t("billing.loading")}</p>
      </div>
    );
  }

  return (
    <div className="ed-billing hd-page" data-screen="editor-billing-details">
      <HubPageHead title={t("billing.details_title")} backLabel={backLabel ?? t("billing.back")} onBack={onBack} />
      <p className="ed-muted">{t("billing.details_intro")}</p>

      {saveError && <p className="editor-offer-error">{saveError}</p>}

      <div className="ed-avail-fields">
        <Field label={t("billing.f_legal_name")} value={draft.legalName} onChange={(v) => set("legalName", v)} error={fieldError("legalName")} disabled={busy} />
        <Field label={t("billing.f_account_holder")} value={draft.accountHolder} onChange={(v) => set("accountHolder", v)} error={fieldError("accountHolder")} disabled={busy} />
        <Field label={t("billing.f_address1")} value={draft.addressLine1} onChange={(v) => set("addressLine1", v)} error={fieldError("addressLine1")} disabled={busy} />
        <Field label={t("billing.f_address2")} value={draft.addressLine2} onChange={(v) => set("addressLine2", v)} disabled={busy} optional />
        <Field label={t("billing.f_postal_code")} value={draft.postalCode} onChange={(v) => set("postalCode", v)} error={fieldError("postalCode")} disabled={busy} />
        <Field label={t("billing.f_city")} value={draft.city} onChange={(v) => set("city", v)} error={fieldError("city")} disabled={busy} />
        <label className="ed-avail-field">
          <span>{t("billing.f_country")}</span>
          {/* The company DB stores the ISO code (DE), so this is a picker, named in the editor's language - never free text. */}
          <AppSelect
            ariaLabel={t("billing.f_country")}
            value={draft.country}
            onChange={(v) => set("country", v)}
            options={countryOptions(intlLocale(locale))}
            disabled={busy}
            searchable
            placeholder={t("billing.not_set")}
            searchPlaceholder={t("common.search")}
            filterLabel={t("common.filter_options")}
            noMatchesLabel={t("common.no_matches")}
          />
          {errors.country && <span className="editor-offer-error">{fieldError("country")}</span>}
        </label>

        <label className="ed-avail-field">
          <span>{t("billing.f_payout_method")}</span>
          <AppSelect
            ariaLabel={t("billing.f_payout_method")}
            value={draft.payoutMethod}
            onChange={(v) => set("payoutMethod", v === "bank_account" ? "bank_account" : "iban")}
            options={PAYOUT_METHOD_OPTIONS.map((o) => ({ value: o.value, label: t(o.key) }))}
            disabled={busy}
          />
          {draft.payoutMethod === "bank_account" && <span className="ed-avail-hint">{t("billing.method_bank_account_hint")}</span>}
        </label>

        {draft.payoutMethod === "iban" ? (
          <label className="ed-avail-field">
            <span>IBAN</span>
            {numberEditing || !draft.iban ? (
              <input type="text" value={draft.iban} onChange={(e) => set("iban", e.target.value)} disabled={busy} aria-label="IBAN" />
            ) : (
              <div className="ed-billing-masked">
                <span>{maskIban(draft.iban)}</span>
                <button type="button" className="ed-rev-resubmit" onClick={() => setNumberEditing(true)}>{t("billing.iban_change")}</button>
              </div>
            )}
            {errors.iban && <span className="editor-offer-error">{fieldError("iban")}</span>}
          </label>
        ) : (
          <>
            <Field label={t("billing.f_bank_name")} value={draft.bankName} onChange={(v) => set("bankName", v)} error={fieldError("bankName")} disabled={busy} />
            <label className="ed-avail-field">
              <span>{t("billing.f_account_number")}</span>
              {numberEditing || !draft.accountNumber ? (
                <input type="text" value={draft.accountNumber} onChange={(e) => set("accountNumber", e.target.value)} disabled={busy} aria-label={t("billing.f_account_number")} />
              ) : (
                <div className="ed-billing-masked">
                  <span>{maskIban(draft.accountNumber)}</span>
                  <button type="button" className="ed-rev-resubmit" onClick={() => setNumberEditing(true)}>{t("billing.iban_change")}</button>
                </div>
              )}
              {errors.accountNumber && <span className="editor-offer-error">{fieldError("accountNumber")}</span>}
            </label>
          </>
        )}

        <Field label={t("billing.f_bic")} value={draft.bic} onChange={(v) => set("bic", v)} error={fieldError("bic")} disabled={busy} optional />
        <Field label={t("billing.f_tax_number")} value={draft.taxNumber} onChange={(v) => set("taxNumber", v)} disabled={busy} optional />
        <Field label={t("billing.f_vat_id")} value={draft.vatId} onChange={(v) => set("vatId", v)} disabled={busy} optional />

        <label className="ed-avail-field">
          <span>{t("billing.field_optional", { label: t("billing.f_vat_status") })}</span>
          <AppSelect
            ariaLabel={t("billing.f_vat_status")}
            value={draft.vatStatus}
            onChange={(v) => set("vatStatus", (v === "small_business" || v === "vat_registered" || v === "none" ? v : "") as EditorVatStatus | "")}
            options={VAT_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.key) }))}
            disabled={busy}
            placeholder={t("billing.not_set")}
          />
        </label>
      </div>

      <div className="ed-submit-actions">
        <button className="ed-primary" disabled={busy} onClick={() => void save()}>{busy ? t("billing.saving") : t("billing.save")}</button>
        {saved && <span className="ed-muted">{t("billing.saved")}</span>}
      </div>
    </div>
  );
}

/**
 * The first-open nudge (spec #298). Fetches on mount, independent of the
 * dashboard's own draw - a failed read shows nothing rather than nagging on a
 * glitch, same fail-open spirit `editor-billing-details-routes.ts#GET` itself
 * already has (a read failure there answers `details: null`, so this prompt
 * cannot tell "unavailable" from "never saved" and treats both the same: ask).
 */
export function BillingDetailsPrompt({ onOpen }: { onOpen: () => void }) {
  const [show, setShow] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (readBillingDetailsPromptDismissed(browserVisitStore())) return;
    let live = true;
    void get<EditorBillingDetailsResponse>("/api/editor/billing-details")
      .then((resp) => { if (live && !resp.details) setShow(true); })
      .catch(() => { /* no prompt on a read error - see module doc */ });
    return () => { live = false; };
  }, []);

  if (!show) return null;

  function dismiss() {
    rememberBillingDetailsPromptDismissed(browserVisitStore());
    setShow(false);
  }

  return (
    <div className="ed-modal-backdrop" role="dialog" aria-modal="true" aria-label={t("billing.prompt_title")}>
      <div className="ed-modal ed-card">
        <h3>{t("billing.prompt_title")}</h3>
        <p className="ed-muted">{t("billing.prompt_body")}</p>
        <div className="ed-submit-actions">
          <button className="ed-primary" onClick={() => { setShow(false); onOpen(); }}>{t("billing.prompt_fill")}</button>
          <button className="ed-secondary" onClick={dismiss}>{t("billing.prompt_later")}</button>
        </div>
      </div>
    </div>
  );
}
